import { useParams, Link } from "react-router-dom";
import SiteShell from "@/components/feature/SiteShell";
import Breadcrumb from "@/components/base/Breadcrumb";
import { getCategoryConfig } from "@/hooks/useCatalog";
import { filterTitles } from "@/hooks/useCatalog";
import CategoryListBoard from "./components/CategoryListBoard";

export default function CategoryPage() {
  const { type } = useParams();
  const config = getCategoryConfig(type);

  if (!config) {
    return (
      <SiteShell
        breadcrumb="/category"
        pageTitle="分类不存在 - 长日光阴"
        pageDescription="您访问的分类页面不存在，请返回首页浏览电影与电视剧内容。"
        pageKeywords="长日光阴,影视分类"
      >
        <div className="flex min-h-[70vh] flex-col items-center justify-center px-4 pt-24 text-center">
          <i className="ri-error-warning-line text-4xl text-foreground-400" aria-hidden="true"></i>
          <h1 className="mt-4 font-heading text-xl font-bold text-foreground-950">分类不存在</h1>
          <p className="mt-2 text-sm text-foreground-600">该分类可能已调整，请从首页重新进入。</p>
          <Link
            to="/"
            className="mt-6 rounded-md bg-primary-800 px-5 py-2.5 text-sm text-background-50 whitespace-nowrap cursor-pointer hover:bg-primary-700"
          >
            返回首页
          </Link>
        </div>
      </SiteShell>
    );
  }

  const total = filterTitles({ type: config.type }).length;

  return (
    <SiteShell
      breadcrumb={`/category/${config.type}`}
      pageTitle={`${config.name} - 长日光阴${config.name}分类`}
      pageDescription={`${config.name}分类汇集长日光阴精选的${config.name}作品，涵盖${config.genres
        .filter((g) => g !== "全部")
        .slice(0, 6)
        .join("、")}等类型，可按热门、最新与评分浏览，长日光阴为你持续更新影视资讯与资源推荐。`}
      pageKeywords={`长日光阴,${config.name},${config.name}推荐,${config.name}分类,影视资源`}
    >
      <div className="pt-16 md:pt-[72px]">
        <section className="relative w-full overflow-hidden bg-primary-950">
          <div className="absolute inset-0">
            <img
              src={config.heroImage}
              alt={`${config.name}分类头图`}
              className="h-full w-full object-cover object-top opacity-70"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/85 to-primary-900/50"></div>
          </div>
          <div className="relative z-10 mx-auto max-w-[1440px] px-4 py-10 md:px-8 md:py-14">
            <Breadcrumb
              items={[
                { label: "首页", to: "/" },
                { label: config.name },
              ]}
            />
            <div className="mt-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
              <div>
                <p className="text-[10px] tracking-[0.28em] text-primary-200">{config.englishName}</p>
                <h1 className="mt-2 font-heading text-3xl font-bold text-background-50 md:text-4xl">
                  {config.name}分类
                </h1>
                <p className="mt-3 max-w-2xl text-sm leading-relaxed text-background-200">{config.longDescription}</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-background-50/10 px-4 py-3 backdrop-blur">
                  <p className="font-heading text-xl font-bold text-secondary-400">{total}</p>
                  <p className="mt-0.5 text-[11px] text-primary-200">部作品</p>
                </div>
                <div className="rounded-lg bg-background-50/10 px-4 py-3 backdrop-blur">
                  <p className="font-heading text-xl font-bold text-secondary-400">
                    {config.genres.length - 1}
                  </p>
                  <p className="mt-0.5 text-[11px] text-primary-200">个子类型</p>
                </div>
                <Link
                  to={`/category/${config.type}/list`}
                  className="inline-flex items-center gap-1.5 rounded-md bg-secondary-500 px-4 py-3 text-sm font-medium text-primary-950 whitespace-nowrap transition-colors cursor-pointer hover:bg-secondary-400"
                >
                  高级筛选
                  <i className="ri-arrow-right-line" aria-hidden="true"></i>
                </Link>
              </div>
            </div>
          </div>
        </section>

        <div className="w-full bg-background-100 px-4 py-3 md:px-8">
          <div className="mx-auto flex max-w-[1440px] flex-wrap items-center gap-2 text-xs text-foreground-600">
            <Link to="/" className="hover:text-primary-700 cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            <span className="text-foreground-900">{config.name}</span>
            <span className="ml-auto flex items-center gap-1 text-foreground-500">
              <i className="ri-time-line" aria-hidden="true"></i>
              最近更新：今日
            </span>
          </div>
        </div>

        <CategoryListBoard config={config} />

        <div className="w-full border-t border-background-200 bg-background-100 px-4 py-10 md:px-8">
          <div className="mx-auto max-w-[1440px]">
            <h2 className="font-heading text-lg font-bold text-foreground-950">关于{config.name}分类</h2>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-700">{config.description}</p>
            <div className="mt-6 flex flex-wrap gap-2">
              {config.genres
                .filter((g) => g !== "全部")
                .slice(0, 8)
                .map((genre) => (
                  <Link
                    key={genre}
                    to={`/category/${config.type}?genre=${encodeURIComponent(genre)}`}
                    className="rounded-full bg-background-50 px-3 py-1.5 text-xs text-foreground-700 transition-colors cursor-pointer hover:bg-secondary-100 hover:text-secondary-900"
                  >
                    {genre}
                  </Link>
                ))}
            </div>
          </div>
        </div>
      </div>
    </SiteShell>
  );
}